import React, {
    forwardRef,
    useRef,
    useImperativeHandle,
    useCallback,
} from 'react';
import { isNil, find, isEmpty } from 'lodash';
import { Form } from 'react-bootstrap';
import Autocomplete from 'react-google-autocomplete';
import { useFormContext, Controller } from 'react-hook-form';
import ErrorMessage from 'components/Common/ErrorMessage';
import { useConfigContext } from 'contexts/ConfigProvider';
import { useNodeContext } from 'contexts/NodeProvider';

const AddressFieldType = {
    StreetNumber: 'street_number',
    StreetName: 'route',
    Zipcode: 'postal_code',
    State: 'administrative_area_level_1',
    City: 'locality',
};
interface IAddressInputProps {
    control: any;
    id: string;
    defaultValue: string;
    name: string;
    required: string;
    maxLength: string;
    label: string;
    disabled: boolean;
    inputPath: string;
    stateDisable: boolean;
    zipcodeDisable: boolean;
    registerRef?: boolean;
}
const AddressInputWithController = forwardRef(
    (
        {
            control,
            id,
            defaultValue,
            name,
            required,
            maxLength,
            label,
            disabled,
            inputPath,
            stateDisable,
            zipcodeDisable,
            registerRef = true,
        }: IAddressInputProps,
        ref
    ) => {
        const { setValue, clearErrors } = useFormContext();
        const { googleApiKey } = useConfigContext();
        const { nodesRef } = useNodeContext();

        const _ref = useRef();
        useImperativeHandle(ref, () => _ref.current);

        return (
            <Controller
                control={control}
                name={name}
                defaultValue={defaultValue}
                render={({
                    field: {
                        onChange: handleChange,
                        value: formValue,
                        ref: rhfRef,
                    },
                    fieldState: { error },
                }) => {
                    const onRefChange = useCallback((node) => {
                        if (registerRef) {
                            _ref.current = node;
                            rhfRef = _ref as any;
                            if (nodesRef && nodesRef?.current) {
                                nodesRef.current[name] = node;
                            }
                        }
                    }, []);
                    const handlePlaceSelected = (place) => {
                        if (isNil(place?.address_components)) {
                            return;
                        }
                        const streetNbr = find(
                            place.address_components,
                            ({ types }) =>
                                types.includes(AddressFieldType.StreetNumber)
                        );
                        const streetName = find(
                            place.address_components,
                            ({ types }) =>
                                types.includes(AddressFieldType.StreetName)
                        );

                        if (!isEmpty(streetName)) {
                            clearErrors(`${inputPath}AddressLine1`);
                            setValue(
                                `${inputPath}AddressLine1`,
                                (!isEmpty(streetNbr?.short_name)
                                    ? `${streetNbr?.short_name} `
                                    : '') + streetName?.short_name ?? ''
                            );
                            if (!zipcodeDisable) {
                                clearErrors(`${inputPath}ZipCode`);
                                const zipcode = find(
                                    place.address_components,
                                    ({ types }) =>
                                        types.includes(AddressFieldType.Zipcode)
                                );
                                setValue(
                                    `${inputPath}ZipCode`,
                                    zipcode?.long_name ?? ''
                                );
                            }
                            if (!stateDisable) {
                                clearErrors(`${inputPath}State`);
                                const state = find(
                                    place.address_components,
                                    ({ types }) =>
                                        types.includes(AddressFieldType.State)
                                );

                                setValue(
                                    `${inputPath}State`,
                                    state?.short_name ?? ''
                                );
                            }

                            handleChange(
                                (!isEmpty(streetNbr?.short_name)
                                    ? `${streetNbr?.short_name} `
                                    : '') + streetName?.short_name ?? ''
                            );
                        }
                        clearErrors(`${inputPath}City`);
                        const city = find(
                            place.address_components,
                            ({ types }) => types.includes(AddressFieldType.City)
                        );
                        setValue(`${inputPath}City`, city?.long_name ?? '');
                    };
                    return (
                        <div className="text-field-container">
                            <Form.Group className="text-field">
                                {label && (
                                    <Form.Label
                                        className="text-field-label form-label"
                                        id={`label-inputquestion-${label}`}
                                        htmlFor={name}
                                    >
                                        {label}
                                        <span className="text-danger">
                                            {required ? ' *' : ' '}
                                        </span>
                                    </Form.Label>
                                )}
                                <Autocomplete
                                    ref={onRefChange}
                                    id={id}
                                    name={name}
                                    data-testid={name}
                                    defaultValue={defaultValue}
                                    className="text-field-input form-control"
                                    onPlaceSelected={handlePlaceSelected}
                                    required={required}
                                    value={formValue}
                                    aria-label="Enter address"
                                    options={{
                                        types: ['address'],
                                        componentRestrictions: {
                                            country: 'us',
                                        },
                                    }}
                                    maxLength={maxLength}
                                    disabled={disabled}
                                    apiKey={googleApiKey}
                                    onChange={(e) =>
                                        handleChange(e.currentTarget?.value)
                                    }
                                />
                                <ErrorMessage message={error?.message} />
                            </Form.Group>
                        </div>
                    );
                }}
            />
        );
    }
);
export default AddressInputWithController;
